import { ref, computed, inject } from "vue";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import pdfMake from "pdfmake/build/pdfmake";
import Toast from "@/plugins/Toast";
import { ToastType } from "@/types/toast";
import { useAuthStore } from "@/store/auth.store";
import ToolsService from "@/services/ToolsService";
import { Movimiento } from "@/models/Movimiento";
import { Saving } from "@/models/Saving";

export default class StateOfAccountDialogViewModel {
  public dialog = ref<boolean>(false);
  public accountStatement = ref<Movimiento[]>([]);
  public saving = ref<Saving | null>(null);
  public loadingPdf = ref<boolean>(false);
  public toast: Toast;
  public toolsService = new ToolsService();
  private authStore = useAuthStore();
  private userInfo: Record<string, any> | null;
  public coin = computed(() =>
    this.toolsService.getCoin(this.saving.value?.moneda || 1)
  );

  constructor() {
    this.toast = inject("toast") as Toast;
    this.userInfo = this.authStore.getUserData;
  }

  /**
   * Abre el diálogo con el ahorro seleccionado y sus movimientos.
   * @param saving Datos del ahorro.
   */
  public openDialog(saving: Saving, movimientos: Movimiento[]): void {
    this.saving.value = saving;
    this.accountStatement.value = movimientos || [];
    this.dialog.value = true;
  }

  /**
   * Cierra el diálogo y resetea los datos.
   */
  public closeDialog(): void {
    this.dialog.value = false;
    this.saving.value = null;
    this.accountStatement.value = [];
  }

  /**
   * Descarga el estado de cuenta como PDF.
   */
  public downloadAccountStatement = async (): Promise<void> => {
    this.loadingPdf.value = true;

    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const logoPath = require("@/assets/images/logo.png");
      const { width: logoWidth, height: logoHeight } =
        await this.toolsService.getImageDimensions(logoPath);

      // Convertir la imagen a base64
      const logoBase64 = await this.toolsService.convertImageToBase64(logoPath);

      // Calcular el tamaño proporcional
      const { width, height } = this.toolsService.calculateImageSize(
        logoWidth,
        logoHeight,
        50
      );

      const docDefinition: TDocumentDefinitions = {
        content: [
          {
            image: logoBase64,
            width, // Ancho calculado proporcional
            height, // Altura fija
            alignment: "center",
            margin: [0, 0, 0, 5],
          },

          {
            text: `Estado de Cuenta - ${this.saving.value?.idahorro} - ${this.userInfo?.nom}`,
            style: "header",
            alignment: "center",
          },
          {
            table: {
              headerRows: 1,
              widths: ["auto", "*", "*", "*", "*"],
              body: [
                [
                  { text: "Fecha", style: "tableHeader" },
                  { text: "Hora", style: "tableHeader" },
                  { text: "Concepto", style: "tableHeader" },
                  { text: "Monto", style: "tableHeader" },
                  { text: "Saldo", style: "tableHeader" },
                ],
                ...this.accountStatement.value.map((mov) => [
                  this.toolsService.formatDate(mov.fecharegistro),
                  mov.hora,
                  mov.concepto,
                  `${this.coin.value} ${this.toolsService.getFloat(
                    mov.capital
                  )}`,
                  `${this.coin.value} ${this.toolsService.getFloat(mov.saldo)}`,
                ]),
              ],
            },
          },
        ],
        styles: {
          header: {
            fontSize: 12,
            bold: true,
          },
          tableHeader: {
            bold: true,
            fontSize: 10,
            fillColor: "#f2f2f2",
          },
        },
      };

      const pdfDocGenerator = pdfMake.createPdf(docDefinition);
      const blob = await new Promise<Blob>((resolve) =>
        pdfDocGenerator.getBlob((blob) => resolve(blob))
      );

      const timestamp = new Date().getTime();
      const fileName = `EC_${this.saving.value?.idahorro}_${timestamp}.pdf`;

      const pdfUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(pdfUrl);

      this.toast.showToast(
        "Estado de cuenta descargado con éxito.",
        ToastType.Success
      );
      this.loadingPdf.value = false;
      this.closeDialog();
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      this.toast.showToast(
        "Error al generar el estado de cuenta.",
        ToastType.Error
      );
      this.loadingPdf.value = false;
    }
  };
  public formatDate = (date: string) => {
    return this.toolsService.formatDate(date);
  };
  public getFloat = (_number: number) => {
    return this.toolsService.getFloat(_number);
  };
}
